import { api } from '@/api/client';
import type { BillingStatus } from '@/features/billing/BillingPage';
/**
 * Application shell: workspace switcher, site-scoped navigation, user menu.
 *
 * The shell owns the viewport: the rail and the context bar are fixed, and only
 * the content column scrolls. That is what lets a page pin its own sticky rail
 * — a sticky element needs a scrolling ancestor, and when the whole document
 * scrolls there is nothing for it to stick to.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { QuickNavigation } from '@/components/QuickNavigation';
import { WebsiteJourney } from '@/components/WorkspacePresentation';
import { workspacePresentation } from '@/components/workspacePresentationMeta';
import { Brand } from '@/components/Brand';
import { useAuth } from '@/app/auth';
import { siteApi } from '@/api/endpoints';
import { Badge, Spinner } from '@/components/ui';
import {
  AlertIcon,
  BoltIcon,
  ChevronDownIcon,
  CloseIcon,
  DocumentIcon,
  GaugeIcon,
  GearIcon,
  GlobeIcon,
  InboxIcon,
  LogoutIcon,
  MenuIcon,
  PlugIcon,
  SparkIcon,
  TrendIcon,
} from '@/components/icons';
import { cn } from '@/utils/format';

import type { ReactNode } from 'react';

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  badge?: number;
  badgeTone?: 'danger' | 'warning';
  /** Match this path exactly. "/sites" needs it, or every page inside a site
      lights up the Websites entry as well as the real one. */
  exact?: boolean;
}

const ICON = 'h-4.5 w-4.5';

export function AppLayout() {
  const { siteId } = useParams<{ siteId?: string }>();
  const location = useLocation();
  const { user, activeOrganization, organizations, switchOrganization, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navigationChoice, setNavigationChoice] = useState<{ path: string; group: string | null } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setCompact(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  const menuRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!sidebarOpen) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : triggerRef.current;
    const nodes = () => Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>('a, button, select') ?? []).filter(node => node.getClientRects().length > 0);
    nodes()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSidebarOpen(false);
      if (event.key !== 'Tab') return;
      const all = nodes(); const first = all[0]; const last = all[all.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const onResize = () => { if (window.innerWidth >= 1024) setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => { document.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize); trigger?.focus(); };
  }, [sidebarOpen]);

  // Navigating should always close the mobile drawer, however it happened —
  // a nav click, the back button, or a redirect.
  useEffect(() => {
    setSidebarOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  // A menu that only closes on mouse-leave strands keyboard and touch users.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const billing = useQuery({
    queryKey: ['billing', activeOrganization?.id],
    queryFn: () => api.get<BillingStatus>('/billing/current'),
    enabled: Boolean(activeOrganization),
    refetchInterval: 30_000,
  });
  const billingExempt = ['/billing', '/settings', '/admin'].includes(location.pathname);
  const hasAccess = billing.data?.has_access === true;
  const sitesQuery = useQuery({
    queryKey: ['sites', 'nav'],
    enabled: hasAccess,
    queryFn: () => siteApi.list({ page_size: 50 }),
    staleTime: 60_000,
  });

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', siteId],
    queryFn: () => siteApi.dashboard(siteId!),
    enabled: Boolean(siteId) && hasAccess,
    // Keeps the pending-approval count in the nav roughly current.
    refetchInterval: 60_000,
  });

  const activeSite = sitesQuery.data?.items.find((site) => site.id === siteId);

  const globalNav: NavItem[] = [
    { label: 'Websites', to: '/sites', icon: <GlobeIcon className={ICON} />, exact: true },
    {
      label: 'Action center',
      to: '/actions',
      icon: <InboxIcon className={ICON} />,
      badge: dashboardQuery.data?.actions_awaiting_approval,
      badgeTone: 'warning',
    },
    { label: 'Integrations', to: '/integrations', icon: <PlugIcon className={ICON} /> },
    { label: 'Plans & billing', to: '/billing', icon: <GearIcon className={ICON} />, exact: true },
    ...(user?.is_superuser ? [{ label: 'Admin', to: '/admin', icon: <GearIcon className={ICON} />, exact: true }] : []),
    { label: 'Settings', to: '/settings', icon: <GearIcon className={ICON} />, exact: true },
  ];

  const siteNav: NavItem[] = siteId
    ? [
        { label: 'Overview', to: `/sites/${siteId}`, icon: <GaugeIcon className={ICON} /> },
        { label: 'Reports', to: `/sites/${siteId}/reports`, icon: <DocumentIcon className={ICON} /> },
        { label: 'Google setup', to: `/sites/${siteId}/search-setup`, icon: <PlugIcon className={ICON} /> },
        {
          label: 'Issues',
          to: `/sites/${siteId}/issues`,
          icon: <AlertIcon className={ICON} />,
          badge: dashboardQuery.data?.critical_issues,
          badgeTone: 'danger',
        },
        {
          label: 'Opportunities',
          to: `/sites/${siteId}/opportunities`,
          icon: <TrendIcon className={ICON} />,
        },
        { label: 'Keywords', to: `/sites/${siteId}/keywords`, icon: <TrendIcon className={ICON} /> },
        { label: 'SEO roadmap', to: `/sites/${siteId}/roadmap`, icon: <SparkIcon className={ICON} /> },
        { label: 'Pages', to: `/sites/${siteId}/pages`, icon: <DocumentIcon className={ICON} /> },
        {
          label: 'AI analysis',
          to: `/sites/${siteId}/analysis`,
          icon: <SparkIcon className={ICON} />,
        },
        {
          label: 'Actions',
          to: `/sites/${siteId}/actions`,
          icon: <BoltIcon className={ICON} />,
          badge: dashboardQuery.data?.actions_awaiting_approval,
          badgeTone: 'warning',
        },
        { label: 'Settings', to: `/sites/${siteId}/settings`, icon: <GearIcon className={ICON} /> },
      ]
    : [];

  const navigationGroups = [
    { id: 'audit', title: 'Audit & reports', description: 'Find what needs attention', icon: <GaugeIcon className={ICON} />, labels: ['Issues', 'Pages', 'Reports'] },
    { id: 'strategy', title: 'Growth strategy', description: 'Plan your next improvements', icon: <TrendIcon className={ICON} />, labels: ['Opportunities', 'Keywords', 'SEO roadmap', 'AI analysis'] },
    { id: 'setup', title: 'Actions & setup', description: 'Execute and connect', icon: <BoltIcon className={ICON} />, labels: ['Actions', 'Google setup', 'Settings'] },
  ].map(group => ({ ...group, items: siteNav.filter(item => group.labels.includes(item.label)) }));
  const currentGroup = navigationGroups.find(group => group.items.some(item => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)))?.id ?? null;
  const expandedGroup = navigationChoice?.path === location.pathname ? navigationChoice.group : currentGroup;
  const toggleGroup = (group: string) => setNavigationChoice({ path: location.pathname, group: expandedGroup === group ? null : group });

  const initial = (user?.full_name || user?.email || '?').charAt(0).toUpperCase();

  return (
    <div className="rankfix-shell flex h-dvh overflow-hidden bg-canvas">
      <a href="#workspace-content" className="skip-link">Skip to content</a>
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          tabIndex={-1}
          className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-[1px] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      {/* ------------------------------------------------------------- rail */}
      <aside
        ref={sidebarRef}
        id="workspace-navigation"
        inert={compact && !sidebarOpen || undefined}
        aria-hidden={compact && !sidebarOpen || undefined}
        role={sidebarOpen ? "dialog" : undefined}
        aria-modal={sidebarOpen || undefined}
        aria-label="Workspace navigation"
        className={cn(
          siteId && 'is-site-context',
          'workspace-rail fixed inset-y-0 left-0 z-40 flex w-[15.5rem] shrink-0 flex-col bg-rail transition-transform duration-200 lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="rail-brand flex h-20 shrink-0 items-center gap-2.5 px-5">
          <div className="text-white"><Brand /></div>
          <button
            type="button"
            className="ml-auto rounded-lg p-1.5 text-rail-ink-subtle hover:bg-rail-hover hover:text-white lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {organizations.length > 1 ? (
          <div className="px-3 pb-3">
            <label htmlFor="workspace" className="sr-only">
              Workspace
            </label>
            <div className="relative">
              <select
                id="workspace"
                value={activeOrganization?.id ?? ''}
                onChange={(event) => void switchOrganization(event.target.value)}
                className="w-full appearance-none rounded-lg border border-rail-border bg-rail-hover py-2 pr-8 pl-3 text-sm text-rail-ink focus:border-brand"
              >
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2 text-rail-ink-subtle" />
            </div>
          </div>
        ) : activeOrganization ? (
          <div className="px-4 pb-3">
            <p className="truncate text-sm text-rail-ink-subtle">{activeOrganization.name}</p>
          </div>
        ) : null}

        <div className="workspace-stamp"><span className="workspace-monogram">{(activeOrganization?.name ?? 'W').charAt(0).toUpperCase()}</span><div><small>YOUR COMMAND CENTER</small><strong>{activeOrganization?.name ?? 'Workspace'}</strong></div></div>
        <nav aria-label="Main navigation" className="rail-navigation pane-scroll flex-1 space-y-6 px-3 pb-4">
          {siteId ? (
            <>
              <Link to="/sites" className="rail-back"><span aria-hidden="true">←</span> All websites</Link>
              <div className="rail-site-card"><span className="rail-site-symbol"><GlobeIcon className={ICON} /></span><div><small>WEBSITE WORKSPACE</small><strong title={activeSite?.name}>{activeSite?.name ?? 'Website'}</strong></div></div>
              <NavSection items={siteNav.filter(item => item.label === 'Overview')} end />
              <div className="rail-tool-groups">
                {navigationGroups.map(group => <NavigationGroup key={group.id} {...group} expanded={expandedGroup === group.id} active={currentGroup === group.id} onToggle={() => toggleGroup(group.id)} />)}
              </div>
              <NavigationGroup id="workspace" title="Workspace" description="Billing, integrations & account" icon={<GearIcon className={ICON} />} items={globalNav} expanded={expandedGroup === 'workspace'} active={false} onToggle={() => toggleGroup('workspace')} />
            </>
          ) : (
            <>
              <NavSection title="Workspace" items={globalNav.filter(item => !["/billing", "/settings", "/admin"].includes(item.to))} />
              <NavSection title="Manage" items={globalNav.filter(item => ["/billing", "/settings", "/admin"].includes(item.to))} />
            </>
          )}
          {sitesQuery.isLoading ? <div className="px-3"><Spinner className="h-4 w-4 text-rail-ink-subtle" /></div> : null}
        </nav>
        {!siteId && <Link to="/billing" className="rail-plan"><SparkIcon /><span><strong>Make your next move</strong><small>Explore your plan & usage</small></span><span aria-hidden="true">↗</span></Link>}

        {/* ------------------------------------------------------ user menu */}
        <div ref={menuRef} className="rail-account relative shrink-0 border-t border-rail-border p-3">
          {menuOpen ? (
            <div
              className="absolute bottom-full left-3 mb-2 w-[13rem] overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-overlay"
              role="menu"
            >
              <div className="border-b border-border-subtle px-3 py-2.5">
                <p className="truncate text-sm font-medium text-ink">{user?.full_name}</p>
                <p className="truncate text-xs text-ink-subtle">{user?.email}</p>
              </div>
              <div className="p-1">
                <NavLink
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ink-muted hover:bg-surface-muted hover:text-ink"
                >
                  <GearIcon className="h-4 w-4" />
                  Settings
                </NavLink>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-ink-muted hover:bg-surface-muted hover:text-ink"
                >
                  <LogoutIcon className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-rail-hover"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-xs font-semibold text-white">
              {initial}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-rail-ink">
                {user?.full_name ?? 'Account'}
              </span>
              <span className="block truncate text-xs text-rail-ink-subtle">{user?.email}</span>
            </span>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-rail-ink-subtle" />
          </button>
        </div>
      </aside>

      {/* --------------------------------------------------- content column */}
      <div className="workspace-column flex min-w-0 flex-1 flex-col" inert={sidebarOpen || undefined}>
        <header className="workspace-topbar z-10 flex h-16 shrink-0 items-center gap-3 border-b border-border-subtle bg-surface px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-ml-1 rounded-lg p-2 text-ink-muted hover:bg-surface-muted lg:hidden"
            onClick={() => setSidebarOpen(true)}
            ref={triggerRef}
            aria-label="Open navigation"
            aria-controls="workspace-navigation"
            aria-expanded={sidebarOpen}
          >
            <MenuIcon className="h-5 w-5" />
          </button>

          <div className="workspace-location"><small>{activeSite ? "Website workspace" : "Your command center"}</small>
          {activeSite ? (
            <div className="flex min-w-0 items-center gap-2.5">
              <GlobeIcon className="h-4 w-4 shrink-0 text-ink-subtle" />
              <span className="truncate text-sm font-medium text-ink">{activeSite.name}</span>
              {activeSite.health_score !== null ? (
                <Badge tone={healthTone(activeSite.health_score)}>
                  {activeSite.health_score}/100
                </Badge>
              ) : null}
            </div>
          ) : (
            <span className="truncate text-sm font-medium text-ink">
              {sectionLabel(location.pathname)}
            </span>
          )}

          </div>
          <div className="topbar-actions ml-auto flex items-center gap-2">
            <QuickNavigation items={[...globalNav, ...siteNav.map(item => ({ ...item, label: `${activeSite?.name ?? 'Website'} / ${item.label}` })), { label: 'Connect a website', to: '/sites/new', icon: <GlobeIcon /> }]} />
            <Link to="/sites/new" className="connect-shortcut" aria-label="Connect website"><span aria-hidden="true">+</span><span>Connect website</span></Link>
            {activeOrganization ? (
              <Badge tone="neutral" className="capitalize">
                {activeOrganization.plan_tier}
              </Badge>
            ) : null}
          </div>
        </header>

        <main id="workspace-content" tabIndex={-1} className="workspace-content pane-scroll flex-1">
          <div className={`workspace-page workspace-screen screen-${workspacePresentation(location.pathname).area} mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-6 lg:px-10`}>
            {siteId && hasAccess && <WebsiteJourney siteId={siteId} pathname={location.pathname} />}
            {billingExempt || hasAccess ? <Outlet /> : billing.isLoading ? <p className="p-6">Checking your subscription…</p> : billing.isError ? <p role="alert" className="p-6">Could not check your subscription. Please reload.</p> : <Navigate to="/billing" replace />}
          </div>
        </main>

        {/* Outside the scroll area on purpose. It keeps the disclaimer in view
            rather than buried at the end of a long page, and it leaves the
            scrollport ending exactly where the content does — which is what
            lets a page's sticky rail pin against the bottom of its column
            instead of being pushed up out of sight. */}
        <footer className="workspace-footer shrink-0 border-t border-border-subtle bg-surface px-4 py-2.5 sm:px-6 lg:px-8">
          <p className="text-xs text-ink-subtle">
            SEO RankFix / Clarity into action. Evidence-based insights; search rankings are not guaranteed.
          </p>
        </footer>
        <nav className="mobile-dock" aria-label="Quick navigation"><DockLink to={siteId ? `/sites/${siteId}` : '/sites'} label={siteId ? 'Overview' : 'Websites'} icon={<GlobeIcon />} end /><DockLink to={siteId ? `/sites/${siteId}/issues` : '/integrations'} label={siteId ? 'Issues' : 'Connect'} icon={siteId ? <AlertIcon /> : <PlugIcon />} /><DockLink to={siteId ? `/sites/${siteId}/actions` : '/actions'} label="Actions" icon={<BoltIcon />} /><button onClick={() => setSidebarOpen(true)} aria-label="Open all navigation"><MenuIcon /><span>More</span></button></nav>
      </div>
    </div>
  );
}

function NavigationGroup({ id, title, description, icon, items, expanded, active, onToggle }: {
  id: string; title: string; description: string; icon: ReactNode; items: NavItem[];
  expanded: boolean; active: boolean; onToggle: () => void;
}) {
  const badge = items.reduce((total, item) => total + (item.badge ?? 0), 0);
  return <section className={cn('rail-tool-group', active && 'has-current-page')}>
    <button type="button" className="rail-group-toggle" aria-expanded={expanded} aria-controls={`rail-group-${id}`} onClick={onToggle}>
      <span className="rail-group-icon">{icon}</span><span className="rail-group-copy"><strong>{title}</strong><small>{description}</small></span>
      {badge > 0 && <span className="rail-group-count" aria-label={`${badge} items needing attention`}>{badge}</span>}
      <ChevronDownIcon className="rail-group-chevron" />
    </button>
    <div id={`rail-group-${id}`} className="rail-group-links" hidden={!expanded}><NavSection items={items} end /></div>
  </section>;
}

function NavSection({
  title,
  items,
  end = false,
}: {
  title?: string;
  items: NavItem[];
  end?: boolean;
}) {
  return (
    <div>
      {title ? (
        <p className="mb-2 truncate px-3 text-[11px] font-semibold tracking-wider text-rail-ink-subtle uppercase">
          {title}
        </p>
      ) : null}
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.exact ?? (end && item.to.split('/').length === 3)}
              className={({ isActive }) =>
                cn(
                  'workspace-nav-link flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-colors',
                  isActive
                    ? 'is-active bg-rail-active font-medium text-white'
                    : 'text-rail-ink-subtle hover:bg-rail-hover hover:text-rail-ink',
                )
              }
            >
              <span className="shrink-0">{item.icon}</span>
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.badge ? (
                <span
                  className={cn(
                    'tnum shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white',
                    item.badgeTone === 'warning' ? 'bg-warning' : 'bg-danger',
                  )}
                >
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Names the current area when no site is in scope, so the bar reads as a
    location rather than an empty strip. */
function sectionLabel(pathname: string): string {
  if (pathname.startsWith('/sites/new')) return 'Connect a website';
  if (pathname.startsWith('/sites')) return 'Websites';
  if (pathname.startsWith('/actions')) return 'Action center';
  if (pathname.startsWith('/integrations')) return 'Integrations';
  if (pathname.startsWith('/billing')) return 'Plans & billing';
  if (pathname.startsWith('/admin')) return 'Administration';
  if (pathname.startsWith('/settings')) return 'Settings';
  return 'SEO RankFix';
}

function healthTone(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 75) return 'success';
  if (score >= 55) return 'warning';
  return 'danger';
}

function DockLink({ to, label, icon, end = false }: { to: string; label: string; icon: ReactNode; end?: boolean }) {
  return <NavLink to={to} end={end} className={({ isActive }) => cn('dock-link', isActive && 'is-active')}>{icon}<span>{label}</span></NavLink>;
}
